import React, { useState, useRef, useEffect } from "react";
import { sendOtp, verifyOtp, setAuthToken } from "../../services";
import {
  Button,
  Alert,
  Spinner,
  FormGroup,
  FormLabel,
} from "../../styles/SharedStyles";
import { TEXTS, formatText } from "../../constants";
import logoSvg from "../../assets/dark-logo.svg";
import {
  VerificationContainer,
  VerificationHeader,
  Logo,
  HeaderTitle,
  HeaderSubtitle,
  VerificationCard,
  Subtitle,
  PhoneInputWrapper,
  PhonePrefix,
  PhoneInput,
  PhoneMaskedText,
  OtpContainer,
  OtpInputs,
  OtpInput,
  ResendLink,
} from "./SignPage.styles";

function PhoneVerification({
  token,
  clientName,
  clientPhoneMasked,
  onVerified,
}) {
  const [step, setStep] = useState("phone"); // 'phone' or 'otp'
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [devOtp, setDevOtp] = useState(null);

  const otpRefs = useRef([]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Handle phone submission
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate phone
    const cleanPhone = phone.replace(/\D/g, "");
    if (cleanPhone.length < 9 || cleanPhone.length > 10) {
      setError(TEXTS.VERIFICATION.ERRORS.INVALID_PHONE);
      return;
    }

    try {
      setLoading(true);
      const response = await sendOtp(token, cleanPhone);

      // Development mode - show OTP
      if (response.data._devOtp) {
        setDevOtp(response.data._devOtp);
      }

      setStep("otp");
      setResendCooldown(60);
    } catch (err) {
      setError(err.message || TEXTS.VERIFICATION.ERRORS.SEND_FAILED);
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  // Handle OTP paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (pastedData.length === 6) {
      setOtp(pastedData.split(""));
      otpRefs.current[5]?.focus();
    }
  };

  // Handle OTP backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Handle OTP submission
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setError(TEXTS.VERIFICATION.ERRORS.INCOMPLETE_OTP);
      return;
    }

    try {
      setLoading(true);
      const cleanPhone = phone.replace(/\D/g, "");
      const response = await verifyOtp(token, cleanPhone, otpCode);

      // Save JWT token
      setAuthToken(response.data.token);

      // Notify parent
      onVerified();
    } catch (err) {
      setError(err.message || TEXTS.VERIFICATION.ERRORS.INVALID_OTP);
      // Clear OTP on error
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Handle resend
  const handleResend = async () => {
    if (resendCooldown > 0) return;

    try {
      setLoading(true);
      setError(null);
      const cleanPhone = phone.replace(/\D/g, "");
      const response = await sendOtp(token, cleanPhone);

      if (response.data._devOtp) {
        setDevOtp(response.data._devOtp);
      }

      setOtp(["", "", "", "", "", ""]);
      setResendCooldown(60);
      otpRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message || TEXTS.VERIFICATION.ERRORS.SEND_FAILED);
    } finally {
      setLoading(false);
    }
  };

  // Format resend countdown text
  const getResendText = () => {
    if (resendCooldown > 0) {
      return formatText(TEXTS.VERIFICATION.OTP_RESEND_COUNTDOWN, {
        seconds: resendCooldown,
      });
    }
    return TEXTS.VERIFICATION.OTP_RESEND;
  };

  return (
    <VerificationContainer>
      <VerificationHeader>
        <Logo src={logoSvg} alt="DevCraft" />
        <HeaderTitle>{TEXTS.APP.NAME}</HeaderTitle>
        <HeaderSubtitle>{TEXTS.APP.TAGLINE}</HeaderSubtitle>
      </VerificationHeader>

      <VerificationCard>
        {step === "phone" ? (
          <>
            <h2>
              {TEXTS.VERIFICATION.GREETING} {clientName}
            </h2>
            <Subtitle>{TEXTS.VERIFICATION.PHONE_PROMPT}</Subtitle>

            {clientPhoneMasked && (
              <PhoneMaskedText>
                {TEXTS.VERIFICATION.PHONE_REGISTERED}
                <strong style={{ margin: "0 4px" }} dir="ltr">
                  {clientPhoneMasked}
                </strong>
              </PhoneMaskedText>
            )}

            <form onSubmit={handlePhoneSubmit}>
              <FormGroup>
                <FormLabel>{TEXTS.VERIFICATION.PHONE_LABEL}</FormLabel>
                <PhoneInputWrapper>
                  <PhonePrefix>{TEXTS.VERIFICATION.PHONE_PREFIX}</PhonePrefix>
                  <PhoneInput
                    type="tel"
                    placeholder={TEXTS.VERIFICATION.PHONE_PLACEHOLDER}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={loading}
                    autoComplete="tel"
                    dir="ltr"
                  />
                </PhoneInputWrapper>
              </FormGroup>

              {error && <Alert variant="error">{error}</Alert>}

              <Button
                type="submit"
                variant="primary"
                $block
                $large
                disabled={loading || !phone}
              >
                {loading ? (
                  <Spinner size="20px" color="white" />
                ) : (
                  TEXTS.VERIFICATION.SEND_OTP
                )}
              </Button>
            </form>
          </>
        ) : (
          <>
            <h2>{TEXTS.VERIFICATION.OTP_TITLE}</h2>
            <Subtitle>
              {TEXTS.VERIFICATION.OTP_SENT_TO} {phone}
            </Subtitle>

            {devOtp && (
              <Alert variant="info" style={{ marginBottom: "16px" }}>
                <strong>{TEXTS.VERIFICATION.OTP_DEV_MODE}</strong>{" "}
                {TEXTS.VERIFICATION.OTP_CODE_LABEL} {devOtp}
              </Alert>
            )}

            <form onSubmit={handleOtpSubmit}>
              <OtpContainer>
                <OtpInputs onPaste={handleOtpPaste}>
                  {otp.map((digit, index) => (
                    <OtpInput
                      key={index}
                      ref={(el) => (otpRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      $filled={!!digit}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      disabled={loading}
                      autoFocus={index === 0}
                    />
                  ))}
                </OtpInputs>
              </OtpContainer>

              {error && (
                <Alert variant="error" style={{ marginTop: "16px" }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                variant="primary"
                $block
                $large
                disabled={loading || otp.some((d) => !d)}
                style={{ marginTop: "24px" }}
              >
                {loading ? (
                  <Spinner size="20px" color="white" />
                ) : (
                  TEXTS.VERIFICATION.OTP_VERIFY
                )}
              </Button>

              <ResendLink>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || loading}
                >
                  {getResendText()}
                </button>
              </ResendLink>

              <Button
                type="button"
                variant="secondary"
                $block
                onClick={() => {
                  setStep("phone");
                  setOtp(["", "", "", "", "", ""]);
                  setError(null);
                }}
                disabled={loading}
                style={{ marginTop: "12px" }}
              >
                {TEXTS.COMMON.BACK}
              </Button>
            </form>
          </>
        )}
      </VerificationCard>
    </VerificationContainer>
  );
}

export default PhoneVerification;
